import { NgModule, Component, ChangeDetectionStrategy } from '@angular/core';
import { Routes, RouterModule, UrlMatchResult, UrlSegment } from '@angular/router';
import { AuthGuard } from './core/guards/auth.guard';
import { WorkspaceComponent } from './workspace/workspace.component';
import { GridComponent } from './core/grid/grid.component';
import { WorkspaceScreenComponent } from './workspace/workspace-screen.component';
import { HomePageContainerComponent } from './home-page/home-page-container.component';
import { StatementTrackerComponent } from './DST/statement-tracker/statement-tracker.component';
import { StatementsFiledStatusComponent } from './DST/statements-filed-status/statements-filed-status.component';
import { ShutdownDstAppComponent } from './DST/admin/shutdown-dst-app/shutdown-dst-app.component';
import { CopyLegacyFilesComponent } from './DST/admin/copy-legacy-files/copy-legacy-files.component';
import { CustomReportComponent } from './DST/reports/custom-report/custom-report.component';
import { Sec926ReportComponent } from './DST/reports/sec-926-report/sec-926-report.component';
import { Sec351ReportComponent } from './DST/reports/sec-351-report/sec-351-report.component';
import { Sec368ReportComponent } from './DST/reports/sec-368-report/sec-368-report.component';
import { Sec332ReportComponent } from './DST/reports/sec-332-report/sec-332-report.component';
import { Form8883ReportComponent } from './DST/reports/form-8883-report/form-8883-report.component';
import { Form8594ReportComponent } from './DST/reports/form-8594-report/form-8594-report.component';
import { DiagnosticsReportComponent } from './DST/reports/diagnostics-report/diagnostics-report.component';
import { DisregardedEntitiesComponent } from './DST/reports/disregarded-entities/disregarded-entities.component';
import { CountByBusinessComponent } from './DST/reports/count-by-business/count-by-business.component';
import { NaReportComponent } from './DST/diagnostics/na-report/na-report.component';
import { DataCollectReportComponent } from './DST/diagnostics/data-collect-report/data-collect-report.component';
import { TransfersReviewReportComponent } from './DST/diagnostics/transfers-review-report/transfers-review-report.component';
import { DupsStmtReportComponent } from './DST/diagnostics/dups-stmt-report/dups-stmt-report.component';
import { F304DiagReportComponent } from './DST/diagnostics/f304-diag-report/f304-diag-report.component';
import {
  FormViewDrilldownRedirectGuard,
  FormViewDrilldownHomeGuard,
} from './admin/components/form-view/form-view-drilldown-redirect.guard';

export const componentMapper: any = {
  GridComponent: GridComponent,
};

@Component({
    selector: 'gtw-workspace-maintenance',
    template: `<div class="workspace-message"><i class="fa fa-wrench"></i><p>This workspace is currently under maintenance.</p></div>`,
    styles: [`.workspace-message { text-align: center; padding: 60px 20px; color: #888; } .workspace-message i { font-size: 48px; margin-bottom: 16px; display: block; }`],
    standalone: false,
    changeDetection: ChangeDetectionStrategy.Eager,
})
export class WorkspaceMaintenanceComponent {}

@Component({
    selector: 'gtw-workspace-not-assigned',
    template: `<div class="workspace-message"><i class="fa fa-info-circle"></i><p>You have not been assigned to any workspace. Please contact your administrator.</p></div>`,
    styles: [`.workspace-message { text-align: center; padding: 60px 20px; color: #888; } .workspace-message i { font-size: 48px; margin-bottom: 16px; display: block; }`],
    standalone: false,
    changeDetection: ChangeDetectionStrategy.Eager,
})
export class WorkspaceNotAssignedComponent {}

@Component({
    selector: 'gtw-workspace-not-found',
    template: `<div class="workspace-message"><i class="fa fa-exclamation-triangle"></i><p>The requested workspace was not found.</p></div>`,
    styles: [`.workspace-message { text-align: center; padding: 60px 20px; color: #888; } .workspace-message i { font-size: 48px; margin-bottom: 16px; display: block; }`],
    standalone: false,
    changeDetection: ChangeDetectionStrategy.Eager,
})
export class WorkspaceNotFoundComponent {}

/** Hash URLs like `/#/?client_key=0` yield one empty UrlSegment, not zero. */
function rootSlashRedirectMatcher(segments: UrlSegment[]): UrlMatchResult | null {
  if (segments.length === 1 && segments[0].path === '') {
    return { consumed: segments };
  }
  return null;
}

const routes: Routes = [
  // Hash URLs like `/#/?client_key=0` resolve to one empty segment, not `''`,
  // so `{ path: '', redirectTo: 'home' }` alone never fires on first load —
  // the router outlet stays empty until the user clicks Home in the nav.
  { path: '', redirectTo: 'home', pathMatch: 'full' },
  { matcher: rootSlashRedirectMatcher, redirectTo: 'home', pathMatch: 'full' },
  {
    path: 'home',
    component: HomePageContainerComponent,
    canActivate: [AuthGuard, FormViewDrilldownHomeGuard],
  },
  // PDF drilldown uses `#popup?element_name=...` — legacy ui-router kept the current
  // state; Angular `**` used to send this to `/home`. Redirect to Form Review.
  {
    path: 'popup',
    canActivate: [AuthGuard, FormViewDrilldownRedirectGuard],
    component: HomePageContainerComponent,
  },
  // Legacy FTC state URLs: route directly into workspace screen resolution.
  { path: 'expenseTrans', redirectTo: 'workspace/expenseTrans', pathMatch: 'full' },
  { path: 'defaults', redirectTo: 'workspace/defaults', pathMatch: 'full' },
  { path: 'attributeDemo', redirectTo: 'workspace/attributeDemo', pathMatch: 'full' },
  { path: 'sync-process', redirectTo: 'workspace/sync-process', pathMatch: 'full' },
  { path: 'auto-tag-process', redirectTo: 'workspace/auto-tag-process', pathMatch: 'full' },
  { path: 'auto-tags', redirectTo: 'workspace/auto-tags', pathMatch: 'full' },
  { path: 'manage-jobs', redirectTo: 'workspace/manage-jobs', pathMatch: 'full' },
  { path: '1118-config', redirectTo: 'workspace/1118-config', pathMatch: 'full' },
  { path: 'form-1118-sync', redirectTo: 'workspace/form-1118-sync', pathMatch: 'full' },
  { path: 'trans-catg-sync', redirectTo: 'workspace/trans-catg-sync', pathMatch: 'full' },
  { path: 'expense-a-a-calc-ftc-admin', redirectTo: 'workspace/expense-a-a-calc-ftc-admin', pathMatch: 'full' },
  { path: 'component-defaults-rollover', redirectTo: 'workspace/component-defaults-rollover', pathMatch: 'full' },
  { path: 'assets-roll-over', redirectTo: 'workspace/assets-roll-over', pathMatch: 'full' },
  { path: 'exams-roll-over', redirectTo: 'workspace/exams-roll-over', pathMatch: 'full' },
  { path: 'branchtax-summary', redirectTo: 'workspace/branchtax-summary', pathMatch: 'full' },
  { path: 'branchtax-defaults', redirectTo: 'workspace/branchtax-defaults', pathMatch: 'full' },
  { path: 'withholdtax-defaults', redirectTo: 'workspace/withholdtax-defaults', pathMatch: 'full' },
  { path: 'exp-consol-conf', redirectTo: 'workspace/exp-consol-conf', pathMatch: 'full' },
  { path: 'systemSettings', redirectTo: 'workspace/systemSettings', pathMatch: 'full' },
  { path: 'investment-validations', redirectTo: 'workspace/investment-validations', pathMatch: 'full' },
  { path: '1118-schedule-j-data-entry', redirectTo: 'workspace/1118-schedule-j-data-entry', pathMatch: 'full' },
  { path: 'general-1118-schedule-k', redirectTo: 'workspace/general-1118-schedule-k', pathMatch: 'full' },
  { path: 'passive-1118-schedule-k', redirectTo: 'workspace/passive-1118-schedule-k', pathMatch: 'full' },
  { path: 'branch-1118-schedule-k', redirectTo: 'workspace/branch-1118-schedule-k', pathMatch: 'full' },
  { path: 'gilti-1118-schedule-k', redirectTo: 'workspace/gilti-1118-schedule-k', pathMatch: 'full' },
  { path: 'treaty-1118-schedule-k', redirectTo: 'workspace/treaty-1118-schedule-k', pathMatch: 'full' },
  { path: '901j-1118-schedule-k', redirectTo: 'workspace/901j-1118-schedule-k', pathMatch: 'full' },
  { path: 'ftc-k2-sync', redirectTo: 'workspace/ftc-k2-sync', pathMatch: 'full' },
  { path: 'k3-overrides-allocation', redirectTo: 'workspace/k3-overrides-allocation', pathMatch: 'full' },
  { path: 'coa-diagnostic-fix', redirectTo: 'workspace/coa-diagnostic-fix', pathMatch: 'full' },
  { path: 'pship-ptr-details-k2', redirectTo: 'workspace/pship-ptr-details-k2', pathMatch: 'full' },
  // The "Modules" home screen lives at `/home`. `/workspace/modules` is
  // a breadcrumb-only legacy alias with no real landing page, so anyone
  // who lands on it (refresh, deep link, stray programmatic navigation)
  // gets bounced to `/home` instead of an empty workspace shell.
  { path: 'workspace/modules', redirectTo: 'home', pathMatch: 'full' },
  // PDF Forms (legacy `form-view` / `state-form-view` parents from
  // `ui/app/components/formView/tabWrapper/formViewTab.js`). Both parent
  // states are top-level (NOT children of `workspace`), so uib-router
  // produces URLs like `/form-view/dashboard` and `/state-form-view/dashboard`
  // — they must NOT be reached through the `/workspace/:screenKey` resolver.
  // We mount `WorkspaceScreenComponent` directly with a `screenLink` in
  // route data so its CE-detection path resolves the registered tag
  // (`gtw-form-view-ce`) without requiring a workspace activity tab.
  { path: 'form-view/dashboard', component: WorkspaceScreenComponent, canActivate: [AuthGuard], data: { screenLink: 'form-view.dashboard', isState: false } },
  // Legacy ui-router `form-view.element-view` — drilldown tab code in URL
  // (e.g. `#/form-view/AL8301-IRS1065.OwedAmt?client_key=0`).
  { path: 'form-view/:tabId', component: WorkspaceScreenComponent, canActivate: [AuthGuard], data: { screenLink: 'form-view.dashboard', isState: false } },
  { path: 'state-form-view/dashboard', component: WorkspaceScreenComponent, canActivate: [AuthGuard], data: { screenLink: 'state-form-view.dashboard', isState: true } },
  { path: 'state-form-view/:tabId', component: WorkspaceScreenComponent, canActivate: [AuthGuard], data: { screenLink: 'state-form-view.dashboard', isState: true } },
  {
    path: 'workspace',
    component: WorkspaceComponent,
    canActivate: [AuthGuard],
    children: [
      { path: '', component: GridComponent },
      { path: 'workspace-maintenance', component: WorkspaceMaintenanceComponent },
      { path: 'workspace-not-assigned', component: WorkspaceNotAssignedComponent },
      { path: 'workspace-404', component: WorkspaceNotFoundComponent },
      { path: ':screenKey', component: WorkspaceScreenComponent },
    ],
  },
  // DST module top-level routes (mirrors legacy ui/app/components/DST/**/*.js $stateProvider entries)
  { path: 'statement-tracker', component: StatementTrackerComponent, canActivate: [AuthGuard] },
  { path: 'statement-tracker/:taxYear', component: StatementTrackerComponent, canActivate: [AuthGuard] },
  { path: 'statement-tracker/:taxYear/:pId', component: StatementTrackerComponent, canActivate: [AuthGuard] },
  { path: 'statement-tracker/:taxYear/:pId/:cId', component: StatementTrackerComponent, canActivate: [AuthGuard] },
  { path: 'dst-statements-filed-status', component: StatementsFiledStatusComponent, canActivate: [AuthGuard] },
  { path: 'shutdown-dst-app', component: ShutdownDstAppComponent, canActivate: [AuthGuard] },
  { path: 'copy-legacy-files', component: CopyLegacyFilesComponent, canActivate: [AuthGuard] },
  { path: 'custom-report', component: CustomReportComponent, canActivate: [AuthGuard] },
  { path: 'section-926-report', component: Sec926ReportComponent, canActivate: [AuthGuard] },
  { path: 'section-351-report', component: Sec351ReportComponent, canActivate: [AuthGuard] },
  { path: 'section-368-report', component: Sec368ReportComponent, canActivate: [AuthGuard] },
  { path: 'section-332-report', component: Sec332ReportComponent, canActivate: [AuthGuard] },
  { path: 'form-8883-report', component: Form8883ReportComponent, canActivate: [AuthGuard] },
  { path: 'form-8594-report', component: Form8594ReportComponent, canActivate: [AuthGuard] },
  { path: 'diagnostics-report', component: DiagnosticsReportComponent, canActivate: [AuthGuard] },
  { path: 'disregarded-entities', component: DisregardedEntitiesComponent, canActivate: [AuthGuard] },
  { path: 'count-by-business', component: CountByBusinessComponent, canActivate: [AuthGuard] },
  { path: 'na-report', component: NaReportComponent, canActivate: [AuthGuard] },
  { path: 'data-collect-report', component: DataCollectReportComponent, canActivate: [AuthGuard] },
  { path: 'transfers-review-report', component: TransfersReviewReportComponent, canActivate: [AuthGuard] },
  { path: 'dst-dups-diag', component: DupsStmtReportComponent, canActivate: [AuthGuard] },
  { path: 'f304-diag-report', component: F304DiagReportComponent, canActivate: [AuthGuard] },
];

@NgModule({
  declarations: [
    WorkspaceMaintenanceComponent,
    WorkspaceNotAssignedComponent,
    WorkspaceNotFoundComponent,
  ],
  imports: [RouterModule.forRoot(routes, { useHash: true })],
  exports: [RouterModule],
})
export class AppRoutingModule {}

/**
 * Wildcard catch-all module. MUST be imported LAST in AppModule
 * so that all feature module routes (forChild) are registered first.
 */
const wildcardRoutes: Routes = [
  { path: '**', redirectTo: 'home' },
];

@NgModule({
  imports: [RouterModule.forChild(wildcardRoutes)],
  exports: [RouterModule],
})
export class WildcardRoutingModule {}
